import React, { useEffect, useMemo, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER = [-27.451163, -58.986510];

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

function nearlyEqual(a, b, eps = 1e-7) {
    return Math.abs(Number(a) - Number(b)) < eps;
}

function MapViewSync({ lat, lng, zoom, loading }) {
    const map = useMap();
    const containerRef = useRef(null);

    useEffect(() => {
        map.invalidateSize();
    }, [map]);

    useEffect(() => {
        if (!loading) {
            const t = setTimeout(() => map.invalidateSize(), 50);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [map, loading]);

    useEffect(() => {
        const el = map.getContainer();
        containerRef.current = el;
        if (typeof ResizeObserver === 'undefined' || !el) return undefined;
        const ro = new ResizeObserver(() => {
            map.invalidateSize();
        });
        ro.observe(el.parentElement || el);
        return () => ro.disconnect();
    }, [map]);

    useEffect(() => {
        if (lat == null || lng == null) return;
        const center = map.getCenter();
        const samePos = nearlyEqual(center.lat, lat) && nearlyEqual(center.lng, lng);
        const sameZoom = map.getZoom() === zoom;
        if (samePos && sameZoom) return;
        map.setView([lat, lng], zoom, { animate: true });
    }, [map, lat, lng, zoom]);

    return null;
}

function MapClickHandler({ disabled, onPick }) {
    useMapEvents({
        click(e) {
            if (disabled) return;
            onPick?.(e.latlng.lat, e.latlng.lng);
        },
    });
    return null;
}

/**
 * Mapa OSM embebido con pin controlado (sin geocode propio).
 */
export default function OsmLocationMap({
    lat = null,
    lng = null,
    onChange,
    zoom = 15,
    height = 280,
    disabled = false,
    loading = false,
    className,
}) {
    const hasPin = lat != null && lng != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
    const center = useMemo(
        () => (hasPin ? [Number(lat), Number(lng)] : DEFAULT_CENTER),
        [hasPin, lat, lng]
    );
    const interactionDisabled = disabled || loading;

    const emitPick = (pickLat, pickLng) => {
        if (interactionDisabled) return;
        onChange?.({
            lat: pickLat,
            lng: pickLng,
            origen_coords: 'mapa',
        });
    };

    return (
        <Box
            className={className}
            sx={{
                position: 'relative',
                width: '100%',
                height: `${height}px`,
                borderRadius: 1,
                overflow: 'hidden',
                border: (t) => `1px solid ${t.palette.divider}`,
                zIndex: 0,
                isolation: 'isolate',
                opacity: disabled ? 0.7 : 1,
                touchAction: 'none',
                WebkitTransform: 'translateZ(0)',
                '& .leaflet-container': {
                    width: '100%',
                    height: '100%',
                    fontFamily: 'inherit',
                },
            }}
        >
            <MapContainer
                center={center}
                zoom={zoom}
                scrollWheelZoom={!interactionDisabled}
                style={{ width: '100%', height: '100%' }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapViewSync
                    lat={hasPin ? Number(lat) : null}
                    lng={hasPin ? Number(lng) : null}
                    zoom={zoom}
                    loading={loading}
                />
                <MapClickHandler disabled={interactionDisabled} onPick={emitPick} />
                {hasPin && (
                    <Marker
                        position={[Number(lat), Number(lng)]}
                        draggable={!interactionDisabled}
                        eventHandlers={{
                            dragend: (e) => {
                                const pos = e.target.getLatLng();
                                emitPick(pos.lat, pos.lng);
                            },
                        }}
                    />
                )}
            </MapContainer>

            {loading && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        bgcolor: 'rgba(255,255,255,0.72)',
                        zIndex: 1000,
                        pointerEvents: 'none',
                        WebkitTransform: 'translateZ(0)',
                    }}
                >
                    <CircularProgress size={36} />
                    <Typography variant="body2" color="text.secondary">
                        Se está ubicando la dirección…
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
